import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { usePlacesWidget } from "react-google-autocomplete";
import { Alert, Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Snackbar, TextField } from "@mui/material";
import { MapPin } from "react-feather";
import ManualLocationDialog from "../../../admin/components/popups/Manuallocationdialog";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { authData, authCustomerData, withCustomerAuth } from "../../../admin/components/GetAuth";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import { useServiceCharge } from "../../../utils/bookingCharges";
import { companyCountries } from "../../../utils/companyCountries";
import { belowMinimum, checkout, formatPrice, getCart, PAYMENT_METHODS } from "../../../frontend/pages/shop/shopApi";
import MinOrderNotice from "../../../frontend/pages/shop/components/MinOrderNotice";
import styles from "../../../frontend/pages/shop/shop.module.css";
import CustomerShell from "./CustomerShell";

const NEW_ADDRESS = "new";
const NOTES_MAX = 500;

/**
 * Address box with Google Places suggestions. usePlacesWidget attaches to the input once, when it mounts,
 * so it lives in its own component that mounts together with the input (the page first shows a loader).
 * country: ISO code the suggestions are limited to (local delivery is in the company's pickup country).
 */
const AddressAutocomplete = ({ value, country, error, onType, onPlace }) => {
  // Google keeps the first callback it is given; this ref always points at the latest one
  const onPlaceRef = useRef(onPlace);
  onPlaceRef.current = onPlace;

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      if (!place?.geometry) return; // Enter pressed without picking a suggestion
      let postCode = "";
      (place.address_components || []).forEach((item) => {
        if (item.types.indexOf("postal_code") > -1) postCode = item.long_name;
      });
      onPlaceRef.current({ address: place.formatted_address, post_code: postCode, latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng() });
    },
    options: { types: ["geocode"], fields: ["formatted_address", "geometry", "address_components"], componentRestrictions: { country: country.toLowerCase() } },
  });

  return (<TextField
    fullWidth
    size="small"
    label="Address*"
    inputRef={ref}
    placeholder="Enter a location"
    value={value}
    onChange={(e) => onType(e.target.value)}
    // Enter picks the highlighted suggestion instead of doing anything else on the page
    onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
    error={error}
    autoComplete="off"
    // Keep the label up so the placeholder shows before typing
    InputLabelProps={{ shrink: true }}
  />);
};

// Delivery address, date and payment choice; the order becomes a local delivery booking
const CustomerCheckout = () => {
  const navigate = useNavigate();
  const auth = authData();
  const customer = authCustomerData();
  const { websiteData } = useWebsite();
  useSiteBranding("Checkout", websiteData);

  const [cart, setCart] = useState(null);
  const [charges, setCharges] = useState(undefined);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState("");
  const [address, setAddress] = useState({ name: customer?.name || "", phone: customer?.phone || "", email: customer?.email || "", address: "", post_code: "", latitude: null, longitude: null });
  const [deliveryDate, setDeliveryDate] = useState(dayjs().add(1, "day"));
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState("");
  // Drag-a-pin popup for addresses the suggestions can't find
  const [mapOpen, setMapOpen] = useState(false);
  // One-tap delivery days: tomorrow and the five days after
  const quickDays = Array.from({ length: 6 }, (_, i) => dayjs().add(i + 1, "day"));

  useEffect(() => {
    const headers = { Authorization: `Bearer ${auth?.api_token}` };
    getCart().then((res) => {
      if (!res.data.items.length) navigate("/customer/shop/cart", { replace: true });
      setCart(res.data);
    }).catch(() => setCart({ items: [], subtotal: 0, count: 0 }));

    axios.get(process.env.REACT_APP_API_URL + "v1/customer/booking-charges", { headers }).then((response) => {
      setCharges(response.data.status === "SUCCESS" ? (response.data.data?.charges || null) : null);
    }).catch(() => setCharges(null));

    // Saved addresses (first one is the customer's own address)
    axios.get(process.env.REACT_APP_API_URL + "v1/customer/adresses/pickup", { headers }).then((response) => {
      if (response.data.status === "SUCCESS") {
        const list = (response.data.data || []).filter((a) => a.address);
        setAddresses(list);
        if (list.length) pickAddress(list[0].value, list);
        else setAddressId(NEW_ADDRESS);
      }
    }).catch(() => setAddressId(NEW_ADDRESS));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickAddress = (value, list = addresses) => {
    setAddressId(value);
    setErrors({});
    if (value === NEW_ADDRESS) {
      setAddress((a) => ({ ...a, address: "", post_code: "", latitude: null, longitude: null }));
      return;
    }
    const a = list.find((x) => String(x.value) === String(value));
    if (a) setAddress({ name: a.name || "", phone: a.phone || "", email: a.email || customer?.email || "", address: a.address, post_code: a.post_code || "", latitude: a.latitude, longitude: a.longitude });
  };

  // A different address than the saved one is delivered to as a new address (and added to the address book)
  const changeAddress = (fields) => {
    const saved = addresses.find((x) => String(x.value) === String(addressId));
    if (addressId !== NEW_ADDRESS && (!saved || saved.address !== fields.address)) setAddressId(NEW_ADDRESS);
    setAddress((a) => ({ ...a, ...fields }));
    setErrors((e) => ({ ...e, address: undefined }));
  };

  // Opens the map on the current address; kept stable so the dialog doesn't reset while open
  const mapCenter = useMemo(() => (address.latitude && address.longitude ? { lat: Number(address.latitude), lng: Number(address.longitude) } : null), [address.latitude, address.longitude]);

  const delivery = useServiceCharge(charges?.delivery, address, "delivery");
  const deliveryCharge = delivery.data?.amount || 0;
  const subtotal = cart?.subtotal || 0;
  const total = subtotal + deliveryCharge;
  const chargePending = charges === undefined || delivery.loading || (charges?.delivery?.enabled && charges?.delivery?.type === "mile" && !delivery.data && !delivery.error);

  const validate = () => {
    const e = {};
    if (!address.name.trim()) e.name = "Please enter a name.";
    if (!address.phone.trim()) e.phone = "Please enter a phone number.";
    if (!address.address.trim()) e.address = "Please choose the delivery address.";
    else if (!address.latitude || !address.longitude) e.address = "Please pick the address from the suggestions, or select it on the map.";
    if (!deliveryDate || !deliveryDate.isValid() || deliveryDate.isBefore(dayjs(), "day")) e.date = "Please choose a delivery date from today onwards.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const placeOrder = () => {
    if (!validate()) return;
    setPlacing(true);
    checkout({
      address: { ...address, value: addressId === NEW_ADDRESS ? null : addressId },
      delivery_date: deliveryDate.format("YYYY-MM-DD"),
      notes,
      payment_method: paymentMethod,
      delivery_miles: delivery.data?.miles ?? null,
    }).then((res) => {
      navigate("/customer/order-history/" + res.data.id, { state: { placed: true } });
    }).catch((e) => {
      setPlacing(false);
      setSnack(e?.response?.data?.message || e.message);
    });
  };

  if (!cart) {
    return (<CustomerShell eyebrow="Shop Now" title="Checkout"><div className={styles.Empty}><CircularProgress size={28} /></div></CustomerShell>);
  }

  return (<CustomerShell eyebrow="Shop Now" title="Checkout" actions={<Button component={Link} to="/customer/shop/cart" className={styles.SecondaryBU}>Back to cart</Button>}>
    <div className={`${styles.Embedded} ${styles.TwoCol}`}>
      <div>
        <div className={styles.Panel} style={{ marginBottom: 16 }}>
          <h2 className={styles.PanelTitle}><span className={styles.PanelNo}>1</span>Delivery address</h2>
          {addresses.length > 0 && (<div className={styles.Field}>
            <FormControl fullWidth size="small">
              <InputLabel>Deliver to</InputLabel>
              <Select label="Deliver to" value={addressId} onChange={(e) => pickAddress(e.target.value)}>
                {addresses.map((a) => <MenuItem key={a.value} value={a.value}>{a.name ? `${a.name}, ` : ""}{a.address}</MenuItem>)}
                <MenuItem value={NEW_ADDRESS}>+ A new address</MenuItem>
              </Select>
            </FormControl>
          </div>)}

          <div className={styles.Field}>
            <div className={styles.AddressRow}>
              <AddressAutocomplete
                value={address.address}
                country={companyCountries(websiteData).pickupCode}
                error={!!errors.address}
                // Typed text has no map position until a suggestion (or a pin) is chosen
                onType={(text) => changeAddress({ address: text, post_code: "", latitude: null, longitude: null })}
                onPlace={changeAddress}
              />
              <IconButton className={styles.MapPinBU} onClick={() => setMapOpen(true)} aria-label="Can't find this address? Select on map" title="Can't find this address? Select on map">
                <MapPin size={18} />
              </IconButton>
            </div>
            {errors.address && <span className={styles.Error}>{errors.address}</span>}
            <span className={styles.Hint}>
              Can't find the address?{" "}
              <button type="button" className={styles.LinkBU} onClick={() => setMapOpen(true)}>Select the location on the map</button>.
              {addressId === NEW_ADDRESS && " New addresses are saved to your address book."}
            </span>
          </div>

          <div className={styles.FieldGrid}>
            <TextField fullWidth size="small" label="Name*" value={address.name} onChange={(e) => setAddress((a) => ({ ...a, name: e.target.value }))} error={!!errors.name} helperText={errors.name} />
            <TextField fullWidth size="small" label="Phone*" type="tel" value={address.phone} onChange={(e) => setAddress((a) => ({ ...a, phone: e.target.value }))} error={!!errors.phone} helperText={errors.phone} />
          </div>
          <div className={styles.Field}>
            <TextField fullWidth size="small" label="Email" type="email" value={address.email} onChange={(e) => setAddress((a) => ({ ...a, email: e.target.value }))} helperText="We send the order updates here." />
          </div>
        </div>

        <div className={styles.Panel} style={{ marginBottom: 16 }}>
          <h2 className={styles.PanelTitle}><span className={styles.PanelNo}>2</span>Delivery date</h2>
          <div className={styles.DayChips} role="radiogroup" aria-label="Delivery date">
            {quickDays.map((d, i) => {
              const active = deliveryDate && deliveryDate.isSame(d, "day");
              return (<button type="button" role="radio" aria-checked={!!active} key={d.format("YYYY-MM-DD")} className={`${styles.DayChip} ${active ? styles.DayChipActive : ""}`} onClick={() => { setDeliveryDate(d); setErrors((e) => ({ ...e, date: undefined })); }}>
                <span>{i === 0 ? "Tomorrow" : d.format("ddd")}</span>
                <strong>{d.format("D MMM")}</strong>
              </button>);
            })}
          </div>
          <div className={styles.Field}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Or choose another date" value={deliveryDate} onChange={(v) => setDeliveryDate(v)} minDate={dayjs()} format="ddd, DD/MM/YYYY" slotProps={{ textField: { fullWidth: true, size: "small", error: !!errors.date, helperText: errors.date } }} />
            </LocalizationProvider>
          </div>
          <div className={styles.Field} style={{ marginBottom: 0 }}>
            <TextField
              fullWidth
              size="small"
              label="Notes for the driver"
              placeholder="e.g. gate code, leave with a neighbour, call on arrival"
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
              helperText={`${notes.length}/${NOTES_MAX}`}
              FormHelperTextProps={{ style: { textAlign: "right" } }}
            />
          </div>
        </div>

        <div className={styles.Panel}>
          <h2 className={styles.PanelTitle}><span className={styles.PanelNo}>3</span>Payment</h2>
          {PAYMENT_METHODS.map((m) => (<label key={m.value} className={`${styles.PayOption} ${paymentMethod === m.value ? styles.PayOptionActive : ""}`}>
            <input type="radio" name="payment_method" value={m.value} checked={paymentMethod === m.value} onChange={() => setPaymentMethod(m.value)} />
            <span>
              <span className={styles.PayLabel}>{m.label}</span>
              <span className={styles.Hint}>{m.hint}</span>
            </span>
          </label>))}
        </div>
      </div>

      <div className={`${styles.Panel} ${styles.StickySummary}`}>
        <h2 className={styles.PanelTitle}>Order summary</h2>
        {cart.items.map((line) => (<div className={styles.SumRow} key={line.id}><span>{line.quantity} × {line.product.title}</span><span>{formatPrice(line.line_total)}</span></div>))}
        <div className={styles.SumRow} style={{ borderTop: "1px solid #edf0f5", marginTop: 6, paddingTop: 10 }}><span>Items</span><span>{formatPrice(subtotal)}</span></div>
        <div className={styles.SumRow}><span>Delivery</span><span>{chargePending ? (address.address ? "Calculating…" : "Choose an address") : formatPrice(deliveryCharge)}</span></div>
        {delivery.error && <span className={styles.Error}>{delivery.error}</span>}
        <div className={`${styles.SumRow} ${styles.SumTotal}`}><span>Total</span><span>{formatPrice(total)}</span></div>
        <MinOrderNotice cart={cart} />
        <Button fullWidth size="large" className={styles.PrimaryBU} style={{ marginTop: 14 }} disabled={placing || chargePending || !!delivery.error || belowMinimum(cart)} onClick={placeOrder}>
          {placing ? "Placing order…" : `Place order · ${formatPrice(total)}`}
        </Button>
        <span className={styles.Hint} style={{ textAlign: "center" }}>{PAYMENT_METHODS.find((m) => m.value === paymentMethod)?.label}. Nothing is charged online.</span>
      </div>
    </div>

    <ManualLocationDialog
      open={mapOpen}
      onClose={() => setMapOpen(false)}
      onConfirm={(data) => {
        changeAddress({ address: data.address, post_code: data.post_code || "", latitude: data.latitude, longitude: data.longitude });
        setMapOpen(false);
      }}
      initialCenter={mapCenter}
      showPostCode={false}
    />

    <Snackbar open={snack !== ""} autoHideDuration={7000} onClose={() => setSnack("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
      <Alert severity="error" variant="filled" onClose={() => setSnack("")}>{snack}</Alert>
    </Snackbar>
  </CustomerShell>);
};

export default withCustomerAuth(CustomerCheckout);
